import React, { useState, useEffect } from 'react';
import PastillePresence from '../common/PastillePresence';
import { usePresence } from '../../hooks/usePresence';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSondeContext } from '../../context/SondeContext';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  apiService,
  ApiService,
  Device,
  MqttCredentials,
} from '../../services/ApiService';
// LOT 2 : import DIRECT du module sans dépendance. Passer par ApiService
// tirerait axios (ESM) dans les tests Jest et ferait échouer la suite.
import {
  ErreurConflitMqtt,
  ErreurVersionPerimee,
  messageErreurApi,
} from '../../services/erreursApi';

// Interface pour un champ de mesure
interface DeviceField {
  field_code: string;
  field_name: string;
  unit?: string;
  min_value?: number | null;
  max_value?: number | null;
  precision_digits?: number;
  is_alertable?: boolean;
}

// Champ vide par défaut
const emptyField: DeviceField = {
  field_code: '',
  field_name: '',
  unit: '',
  min_value: null,
  max_value: null,
  precision_digits: 2,
  is_alertable: true,
};

// Form state interface
interface FormData {
  name: string;
  mqtt_topic: string;
  location: string;
  notes: string;
  generate_password: boolean;
  surveillance_active: boolean;
  fields: DeviceField[];
}

const DeviceManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshData } = useSondeContext();
  
  // États
  const [devices, setDevices] = useState<Device[]>([]);
  const { presence, supervision } = usePresence();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldsDialogOpen, setFieldsDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [mqttCredentials, setMqttCredentials] = useState<MqttCredentials | null>(null);
  const [deviceFields, setDeviceFields] = useState<DeviceField[]>([]);

  // LOT 3.4 — depuis le LOT 3.1, POST/PUT /devices/{id}/fields et
  // PUT /devices/{id}/alerts/{code} exigent `write`. Ne plus PROPOSER ce que
  // l'API refusera. Valeur initiale `true` : tant que /users/me n'a pas
  // repondu on ne bride rien : tant que /users/me n'a pas repondu, aucun
  // controle n'est applique cote interface ; c'est l'API qui refuse.
  const [peutEcrire, setPeutEcrire] = useState(true);

  // LOT 2 — conflit d'identifiant MQTT.
  // `conflitBloquant` : le nom est pris par un boîtier VIVANT, il n'y a rien
  // à proposer, on affiche l'erreur dans le formulaire (et non dans un
  // snackbar qui disparaît pendant que l'exploitant relit son formulaire).
  // `conflitReprise` : le nom a appartenu à un boîtier SUPPRIMÉ ; on demande
  // une confirmation explicite avant de reposter avec le drapeau.
  const [conflitBloquant, setConflitBloquant] = useState<string | null>(null);
  const [conflitReprise, setConflitReprise] = useState<ErreurConflitMqtt | null>(null);
  // LOT 9 (audit J-003) — écriture concurrente sur la fiche en cours d'édition.
  // Le formulaire RESTE OUVERT : le fermer ferait perdre la saisie de
  // l'opérateur.
  const [conflitVersion, setConflitVersion] = useState<ErreurVersionPerimee | null>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    mqtt_topic: '',
    location: '',
    notes: '',
    generate_password: true,
    surveillance_active: true,
    fields: [{ ...emptyField }],
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Charger les données
  const loadData = async () => {
    setLoading(true);
    try {
      const devicesData = await apiService.getAllDevices();
      setDevices(devicesData);
      setError(null);
    } catch (err: any) {
      setError(`Erreur de chargement : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // LOT 3.4 — permissions du porteur du jeton.
  useEffect(() => {
    let actif = true;
    apiService.getCurrentUser().then((utilisateur) => {
      if (actif) {
        setPeutEcrire(ApiService.aLaPermission(utilisateur, 'write'));
      }
    });
    return () => {
      actif = false;
    };
  }, []);

  // Ouvrir le dialog si la route est /new
  useEffect(() => {
    if (location.pathname === '/admin/devices/new') {
      setCreateDialogOpen(true);
    }
  }, [location.pathname]);

  // Créer un device
  // LOT 2 : `reprendreIdentifiant` n'est vrai que lorsque l'exploitant a
  // confirmé dans le dialogue de reprise. Il n'est JAMAIS posé par défaut :
  // reprendre un identifiant rattache l'historique InfluxDB de l'ancien
  // boîtier au nouveau.
  const handleCreate = async (reprendreIdentifiant = false) => {
    setConflitBloquant(null);
    try {
      // Filtrer les champs vides
      const validFields = formData.fields.filter(f => f.field_code && f.field_name);

      const requestData = {
        name: formData.name,
        mqtt_topic: formData.mqtt_topic,
        location: formData.location,
        notes: formData.notes,
        generate_password: formData.generate_password,
        fields: validFields.length > 0 ? validFields : undefined,
        ...(reprendreIdentifiant ? { reutiliser_identifiant_supprime: true } : {}),
      };

      const result = await apiService.createDevice(requestData as any);
      if (result) {
        setConflitReprise(null);
        setMqttCredentials(result.mqtt_credentials);
        setCredentialsDialogOpen(true);
        setCreateDialogOpen(false);
        resetForm();
        loadData();
        refreshData(); // Rafraîchir la sidebar
        navigate('/admin/devices'); // Retourner à la liste
        setSnackbar({ open: true, message: 'Boîtier créé avec succès', severity: 'success' });
      }
    } catch (err: any) {
      if (err instanceof ErreurConflitMqtt) {
        if (err.reprisePossible) {
          // Le nom a appartenu à un boîtier supprimé. Une confirmation est
          // demandée avant de reposter.
          setConflitReprise(err);
          return;
        }
        // Nom pris par un boîtier vivant, ou identifiant invalide : message
        // affiché dans le formulaire ; le snackbar disparaît au bout de 3 s.
        setConflitReprise(null);
        setConflitBloquant(err.message);
        return;
      }
      setSnackbar({ open: true, message: `Erreur: ${messageErreurApi(err)}`, severity: 'error' });
    }
  };

  // Confirmation de reprise d'un identifiant libéré par une suppression.
  const handleConfirmerReprise = async () => {
    setConflitReprise(null);
    await handleCreate(true);
  };

  // Supprimer un device
  const handleDelete = async () => {
    if (!selectedDevice) return;
    try {
      await apiService.deleteDevice(selectedDevice.id);
      setDeleteDialogOpen(false);
      setSelectedDevice(null);
      loadData();
      refreshData(); // Rafraîchir la sidebar
      setSnackbar({ open: true, message: 'Boîtier supprimé', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erreur: ${err.message}`, severity: 'error' });
    }
  };

  // Ouvrir dialog d'édition
  const handleOpenEditDialog = (device: Device) => {
    setSelectedDevice(device);
    setFormData({
      name: device.name || '',
      mqtt_topic: device.mqtt_topic || '',
      location: device.location || '',
      notes: device.notes || '',
      generate_password: false,
      surveillance_active: device.surveillance_active !== false,
      fields: [],
    });
    setEditDialogOpen(true);
  };

  // Mettre à jour un device
  const handleUpdate = async () => {
    if (!selectedDevice) return;
    setConflitVersion(null);
    try {
      // LOT 9 : la version lue à l'ouverture du formulaire part avec la
      // requête. Sans elle l'API répond 428 — c'est volontaire, un client
      // qui ne sait pas gérer les conflits ne doit pas pouvoir écraser la
      // saisie de quelqu'un d'autre en silence.
      const misAJour = await apiService.updateDevice(selectedDevice.id, {
        name: formData.name,
        location: formData.location,
        notes: formData.notes,
        surveillance_active: formData.surveillance_active,
        version: selectedDevice.version,
      });
      setEditDialogOpen(false);
      setSelectedDevice(null);
      resetForm();
      loadData();
      refreshData();
      void misAJour;
      setSnackbar({ open: true, message: 'Boîtier mis à jour', severity: 'success' });
    } catch (err: any) {
      if (err instanceof ErreurVersionPerimee) {
        setConflitVersion(err);
        if (!err.saisieRecuperable) {
          // Le boîtier a été supprimé pendant la saisie : il n'y a plus rien
          // à enregistrer, on ferme et on rafraîchit la liste.
          setEditDialogOpen(false);
          setSelectedDevice(null);
          loadData();
        }
        setSnackbar({ open: true, message: err.message, severity: 'error' });
        return;
      }
      // LOT 2 : une réactivation refusée (409 reactivation_boitier_supprime)
      // doit dire POURQUOI, pas « Request failed with status code 409 ».
      setSnackbar({ open: true, message: `Erreur: ${messageErreurApi(err)}`, severity: 'error' });
    }
  };

  /** LOT 9 — recharge la fiche courante depuis l'API en CONSERVANT la saisie.
   *
   *  L'opérateur récupère la version à jour (donc peut réessayer) sans que
   *  ce qu'il vient de taper disparaisse. « Rechargez la page » lui aurait
   *  fait perdre sa saisie une seconde fois. */
  const handleRechargerFicheEnConflit = async () => {
    if (!selectedDevice) return;
    try {
      const frais = await apiService.getDevice(selectedDevice.id);
      if (frais) {
        setSelectedDevice(frais);
        setConflitVersion(null);
        setSnackbar({
          open: true,
          message: 'Fiche rechargée : vous pouvez enregistrer à nouveau.',
          severity: 'success',
        });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erreur: ${messageErreurApi(err)}`, severity: 'error' });
    }
  };

  // Ouvrir dialog configuration champs
  const handleOpenFieldsDialog = async (device: Device) => {
    setSelectedDevice(device);
    try {
      const fields = await apiService.getDeviceFields(device.id);
      setDeviceFields(fields.length > 0 ? fields : [{ ...emptyField }]);
      setFieldsDialogOpen(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erreur: ${err.message}`, severity: 'error' });
    }
  };

  // Regénérer les identifiants MQTT
  const handleRegenerateMqtt = async (device: Device) => {
    try {
      const credentials = await apiService.regenerateDeviceMqtt(device.id);
      if (credentials) {
        setMqttCredentials(credentials);
        setCredentialsDialogOpen(true);
        setSnackbar({ open: true, message: 'Identifiants MQTT régénérés', severity: 'success' });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erreur: ${err.message}`, severity: 'error' });
    }
  };

  // Fermer le dialog de création
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setConflitBloquant(null);
    setConflitReprise(null);
    resetForm();
    if (location.pathname === '/admin/devices/new') {
      navigate('/admin/devices');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      mqtt_topic: '',
      location: '',
      notes: '',
      generate_password: true,
    surveillance_active: true,
      fields: [{ ...emptyField }],
    });
  };

  // Ajouter un champ au formulaire
  const addField = () => {
    setFormData({
      ...formData,
      fields: [...formData.fields, { ...emptyField }],
    });
  };

  // Supprimer un champ du formulaire
  const removeField = (index: number) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      fields: newFields.length > 0 ? newFields : [{ ...emptyField }],
    });
  };

  // Mettre à jour un champ
  const updateField = (index: number, key: keyof DeviceField, value: any) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], [key]: value };
    setFormData({ ...formData, fields: newFields });
  };

  // Copier dans le presse-papiers
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copié dans le presse-papiers', severity: 'success' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Gestion des boîtiers</Typography>
          <Typography variant="body2" color="text.secondary">
            {devices.length} boîtier{devices.length > 1 ? 's' : ''} configuré{devices.length > 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box>
          <Button startIcon={<RefreshIcon />} onClick={loadData} sx={{ mr: 1 }}>
            Actualiser
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            Nouveau boîtier
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Table des devices */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Emplacement</TableCell>
              <TableCell>Topic MQTT</TableCell>
              <TableCell>Réseau</TableCell>
              <TableCell>Grandeurs</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    Aucun boîtier configuré. Cliquez sur « Nouveau boîtier » pour en ajouter un.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>{device.id}</TableCell>
                  <TableCell><strong>{device.name}</strong></TableCell>
                  <TableCell>{device.location || '-'}</TableCell>
                  <TableCell>
                    <code style={{ fontSize: '0.85em', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                      {device.mqtt_topic}
                    </code>
                  </TableCell>
                  <TableCell>
                    {(device as any).last_hostname || (device as any).last_ip ? (
                      <>
                        <div><strong>{(device as any).last_hostname || '—'}</strong></div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{(device as any).last_ip || ''}</div>
                        {(device as any).last_firmware && (
                          <div style={{ fontSize: '0.75em', color: '#999' }}><code>{(device as any).last_firmware}</code></div>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>jamais connecté</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button 
                      size="small" 
                      startIcon={<SettingsIcon />}
                      onClick={() => handleOpenFieldsDialog(device)}
                    >
                      Configurer
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Présence temps réel (battement ~5 s). L'ancien
                          `device.status` vient d'une colonne SQL écrite au
                          mieux toutes les 120 s : conservé en repli seulement
                          pour les boîtiers sans battement. */}
                      {device.surveillance_active === false && (
                        <Chip label="hors surveillance" size="small" variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                      {presence[device.id] && presence[device.id].etat !== 'inconnu' ? (
                        <PastillePresence presence={presence[device.id]} supervision={supervision} />
                      ) : (
                        <>
                          <CircleIcon
                            sx={{
                              fontSize: 12,
                              color: device.status === 'online' ? 'success.main' : 'error.main',
                            }}
                          />
                          {device.status || 'unknown'}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        data-testid={`modifier-boitier-${device.id}`}
                        onClick={() => handleOpenEditDialog(device)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Regénérer MQTT">
                      <IconButton size="small" onClick={() => handleRegenerateMqtt(device)}>
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedDevice(device);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Création */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Nouveau Boîtier</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* LOT 2 — conflit d'identifiant MQTT bloquant. Affiché DANS le
                formulaire et persistant : l'exploitant doit pouvoir le relire
                pendant qu'il corrige le topic. */}
            {conflitBloquant && (
              <Alert severity="error" data-testid="conflit-mqtt-bloquant">
                {conflitBloquant}
              </Alert>
            )}
            {/* Infos générales */}
            <Typography variant="subtitle2" color="primary">Informations générales</Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="Nom du boîtier"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                placeholder="Ex: 1374 Cyanure Zingage"
              />
              <TextField
                label="Emplacement"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                fullWidth
                placeholder="Ex: Chaine n°2"
              />
            </Box>
            <TextField
              label="Topic MQTT"
              value={formData.mqtt_topic}
              onChange={(e) => setFormData({ ...formData, mqtt_topic: e.target.value })}
              fullWidth
              required
              placeholder="plant/boitier1/raw"
              helperText="Topic MQTT complet du boîtier (ex: plant/boitier1/raw)"
              error={!formData.mqtt_topic}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.generate_password}
                  onChange={(e) => setFormData({ ...formData, generate_password: e.target.checked })}
                />
              }
              label="Générer un mot de passe MQTT unique"
            />
            
            <Divider sx={{ my: 1 }} />
            
            {/* Grandeurs mesurées */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" color="primary">Grandeurs mesurées</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addField}>
                Ajouter
              </Button>
            </Box>
            
            {formData.fields.map((field, index) => (
              <Accordion key={index} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {field.field_name || field.field_code || `Champ ${index + 1}`}
                    {field.unit && ` (${field.unit})`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Code JSON"
                        value={field.field_code}
                        onChange={(e) => updateField(index, 'field_code', e.target.value)}
                        placeholder="temp"
                        helperText="Clé dans le JSON MQTT"
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Nom affiché"
                        value={field.field_name}
                        onChange={(e) => updateField(index, 'field_name', e.target.value)}
                        placeholder="Température"
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Unité"
                        value={field.unit || ''}
                        onChange={(e) => updateField(index, 'unit', e.target.value)}
                        placeholder="°C"
                        size="small"
                        sx={{ flex: 0.5 }}
                      />
                    </Box>
                    <Box display="flex" gap={2} alignItems="center">
                      <TextField
                        label="Seuil min"
                        type="number"
                        value={field.min_value ?? ''}
                        onChange={(e) => updateField(index, 'min_value', e.target.value ? Number(e.target.value) : null)}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Seuil max"
                        type="number"
                        value={field.max_value ?? ''}
                        onChange={(e) => updateField(index, 'max_value', e.target.value ? Number(e.target.value) : null)}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Décimales"
                        type="number"
                        value={field.precision_digits ?? 2}
                        onChange={(e) => updateField(index, 'precision_digits', Number(e.target.value))}
                        size="small"
                        sx={{ flex: 0.5 }}
                        inputProps={{ min: 0, max: 6 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.is_alertable !== false}
                            onChange={(e) => updateField(index, 'is_alertable', e.target.checked)}
                          />
                        }
                        label="Alertes"
                      />
                      {formData.fields.length > 1 && (
                        <IconButton color="error" onClick={() => removeField(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
            
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => handleCreate()}
            disabled={!formData.name || !formData.mqtt_topic}
          >
            Créer le boîtier
          </Button>
        </DialogActions>
      </Dialog>

      {/* LOT 2 — Dialog de reprise d'un identifiant libéré par une
          suppression logique. Sans ce dialogue, le reprovisionnement terrain
          de boitier1..6 passait par du SQL manuel. Le texte énonce ce que la
          reprise implique. */}
      <Dialog
        open={conflitReprise !== null}
        onClose={() => setConflitReprise(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reprendre un identifiant MQTT libéré ?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="conflit-mqtt-reprise">
            {conflitReprise?.message}
          </Alert>
          <Typography variant="body2">
            L'identifiant <strong>{conflitReprise?.mqttUsername}</strong> a appartenu au boîtier
            {' '}<strong>n°{conflitReprise?.deviceId}</strong>, supprimé. Le reprendre rattachera
            l'historique InfluxDB de l'ancien boîtier au nouveau : les mesures antérieures
            seront attribuées au boîtier créé.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Pour repartir d'un historique vierge, annulez et choisissez un autre topic MQTT.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflitReprise(null)}>Annuler</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmerReprise}>
            Reprendre l'identifiant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Édition */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setConflitVersion(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Modifier le boîtier</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {conflitVersion && conflitVersion.saisieRecuperable && (
              <Alert
                severity="warning"
                data-testid="conflit-version"
                action={
                  <Button color="inherit" size="small" onClick={handleRechargerFicheEnConflit}>
                    Recharger la fiche
                  </Button>
                }
              >
                {conflitVersion.message}
                {conflitVersion.etatActuel?.name && (
                  <Box component="div" sx={{ mt: 1 }}>
                    Valeur enregistrée entre-temps :{' '}
                    <strong>{String(conflitVersion.etatActuel.name)}</strong>
                  </Box>
                )}
                <Box component="div" sx={{ mt: 1 }}>
                  Votre saisie est conservée ci-dessous. Rechargez la fiche, puis
                  enregistrez de nouveau pour l'appliquer.
                </Box>
              </Alert>
            )}
            <TextField
              label="Nom du boîtier"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              placeholder="Ex: 1374 Cyanure Zingage"
            />
            <TextField
              label="Emplacement"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              fullWidth
              placeholder="Ex: Chaine n°2"
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.surveillance_active}
                  data-testid="interrupteur-surveillance"
                  onChange={(e) => setFormData({ ...formData, surveillance_active: e.target.checked })}
                />
              }
              label="Surveillance active"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Désactivée, le boîtier reste visible et son état reste tenu à jour,
              mais il n'émet plus d'alerte ni de courriel. À couper pour un
              boîtier déclaré mais pas encore installé.
            </Typography>
            <Alert severity="info" sx={{ mt: 1 }}>
              Le topic MQTT ne peut pas être modifié. Pour changer le topic, supprimez et recréez le boîtier.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setConflitVersion(null); }}>
            Annuler
          </Button>
          <Button variant="contained" onClick={handleUpdate} disabled={!formData.name}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le boîtier <strong>{selectedDevice?.name}</strong> ?
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            Cette action est irréversible. Les alertes associées seront également supprimées.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Configuration Champs */}
      <Dialog open={fieldsDialogOpen} onClose={() => setFieldsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configurer les grandeurs - {selectedDevice?.name}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {deviceFields.map((field, index) => (
              <Accordion key={index} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {field.field_name || field.field_code || `Champ ${index + 1}`}
                    {field.unit && ` (${field.unit})`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Code JSON"
                        value={field.field_code}
                        onChange={(e) => {
                          const updated = [...deviceFields];
                          updated[index] = { ...updated[index], field_code: e.target.value };
                          setDeviceFields(updated);
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Nom affiché"
                        value={field.field_name}
                        onChange={(e) => {
                          const updated = [...deviceFields];
                          updated[index] = { ...updated[index], field_name: e.target.value };
                          setDeviceFields(updated);
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Unité"
                        value={field.unit || ''}
                        onChange={(e) => {
                          const updated = [...deviceFields];
                          updated[index] = { ...updated[index], unit: e.target.value };
                          setDeviceFields(updated);
                        }}
                        size="small"
                        sx={{ flex: 0.5 }}
                      />
                    </Box>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Seuil min"
                        type="number"
                        value={field.min_value ?? ''}
                        onChange={(e) => {
                          const updated = [...deviceFields];
                          updated[index] = { ...updated[index], min_value: e.target.value ? Number(e.target.value) : null };
                          setDeviceFields(updated);
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Seuil max"
                        type="number"
                        value={field.max_value ?? ''}
                        onChange={(e) => {
                          const updated = [...deviceFields];
                          updated[index] = { ...updated[index], max_value: e.target.value ? Number(e.target.value) : null };
                          setDeviceFields(updated);
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
            {/* LOT 3.4 : masque si le compte n'a pas `write` — l'API rendrait 403. */}
            {peutEcrire && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setDeviceFields([...deviceFields, { ...emptyField }])}
              >
                Ajouter un champ
              </Button>
            )}
            {!peutEcrire && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Compte en lecture seule : la modification des champs et des seuils
                d'alerte demande la permission « write ».
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldsDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={!peutEcrire}
            onClick={async () => {
              if (!selectedDevice) return;
              // LOT 3.4 — boucle RESILIENTE. Avant, un 403 sur le 2e champ
              // sortait de la boucle : les champs suivants n'etaient jamais
              // envoyes et l'ecran affichait une erreur generique, laissant une
              // configuration a moitie ecrite sans dire lesquels avaient pris.
              const aEnvoyer = deviceFields.filter((f) => f.field_code);
              const echecs: string[] = [];
              let reussis = 0;
              for (const field of aEnvoyer) {
                try {
                  await apiService.updateDeviceField(selectedDevice.id, field.field_code, field);
                  reussis += 1;
                } catch (err: any) {
                  const code = err?.response?.status;
                  echecs.push(`${field.field_code}${code ? ` (HTTP ${code})` : ''}`);
                }
              }
              if (echecs.length === 0) {
                setSnackbar({
                  open: true,
                  message: `${reussis} champ${reussis > 1 ? 's' : ''} enregistré${reussis > 1 ? 's' : ''}`,
                  severity: 'success',
                });
                setFieldsDialogOpen(false);
              } else {
                setSnackbar({
                  open: true,
                  message: `${reussis} champ sur ${aEnvoyer.length} enregistré — échec : ${echecs.join(', ')}`,
                  severity: 'error',
                });
              }
            }}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Identifiants MQTT */}
      <Dialog open={credentialsDialogOpen} onClose={() => setCredentialsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Identifiants MQTT</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Notez ces informations maintenant. Le mot de passe ne sera plus affiché.
          </Alert>
          {mqttCredentials && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Nom d'utilisateur</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <code style={{ flex: 1, padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                    {mqttCredentials.username}
                  </code>
                  <IconButton size="small" onClick={() => copyToClipboard(mqttCredentials.username)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Mot de passe</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <code style={{ flex: 1, padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                    {mqttCredentials.password}
                  </code>
                  <IconButton size="small" onClick={() => copyToClipboard(mqttCredentials.password)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Topic</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <code style={{ flex: 1, padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                    {mqttCredentials.topic}
                  </code>
                  <IconButton size="small" onClick={() => copyToClipboard(mqttCredentials.topic)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCredentialsDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceManagement;
